import React, { useEffect, useState } from 'react';
import './screenshots.css';
import screenshot1 from '../screenshots-images/screen1.webp';
import screenshot2 from '../screenshots-images/screen2.webp';
import screenshot3 from '../screenshots-images/screen3.webp';
import screenshot4 from '../screenshots-images/screen4.webp';

const items = [
  {
    src: screenshot1,
    caption: 'Guess words with a simple on-screen keyboard',
    altText:
      'Hangman gameplay screen showing a partially guessed word, the hangman drawing, and an on-screen letter keyboard'
  },
  {
    src: screenshot2,
    caption: 'Choose your difficulty and word list',
    altText:
      'Settings screen for choosing easy or hard mode, viewing hanged/rescued score, and changing the word list'
  },
  {
    src: screenshot3,
    caption: 'Get a definition when you need a hint',
    altText: 'Hint screen showing a definition for the current word'
  },
  {
    src: screenshot4,
    caption: 'Build vocabulary one win at a time',
    altText: 'Game won screen reading "Good job! The word is COTENANCY"'
  }
];

const AUTO_ADVANCE_DELAY = 4000;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(REDUCED_MOTION_QUERY).matches;

const Screenshots = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isReducedMotion, setIsReducedMotion] = useState(
    prefersReducedMotion
  );

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;

    const mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    const handleChange = (event) => setIsReducedMotion(event.matches);

    mediaQuery.addEventListener?.('change', handleChange);
    return () => mediaQuery.removeEventListener?.('change', handleChange);
  }, []);

  useEffect(() => {
    if (isReducedMotion || isHovered || isFocusWithin || isPaused) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setActiveIndex((currentIndex) => (currentIndex + 1) % items.length);
    }, AUTO_ADVANCE_DELAY);

    return () => window.clearInterval(interval);
  }, [activeIndex, isFocusWithin, isHovered, isPaused, isReducedMotion]);

  const handleBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsFocusWithin(false);
    }
  };

  return (
    <div
      className="screenshot-carousel"
      role="group"
      aria-roledescription="carousel"
      aria-label="App screenshots"
      aria-live="off"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsFocusWithin(true)}
      onBlur={handleBlur}
    >
      <ul className="screenshots">
        {items.map((item, index) => {
          const isActive = index === activeIndex;

          return (
            <li
              key={index}
              className="screenshot-slide"
              aria-hidden={!isActive}
              data-active={isActive ? 'true' : undefined}
            >
              <img
                src={item.src}
                alt={item.altText}
                width="400"
                height="600"
                loading={index === 0 ? 'eager' : 'lazy'}
              />
            </li>
          );
        })}
      </ul>
      <p className="screenshot-caption" aria-hidden="true">
        {items[activeIndex].caption}
      </p>
      <div className="carousel-controls">
        {!isReducedMotion && (
          <button
            type="button"
            className="carousel-toggle"
            aria-label={
              isPaused
                ? 'Play screenshot carousel'
                : 'Pause screenshot carousel'
            }
            title={isPaused ? 'Play' : 'Pause'}
            onClick={() => setIsPaused((paused) => !paused)}
          >
            <span aria-hidden="true">{isPaused ? '\u25b6' : '\u275a\u275a'}</span>
          </button>
        )}
        <div
          className="carousel-dots"
          role="group"
          aria-label="Choose a screenshot"
        >
          {items.map((item, index) => (
            <button
              key={item.src}
              type="button"
              className="carousel-dot"
              aria-label={`Show screenshot ${index + 1}`}
              aria-current={index === activeIndex ? 'true' : undefined}
              onClick={() => setActiveIndex(index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Screenshots;
